import React from 'react'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ProgressBar from './ProgressBar'
import { progress, isValidated, ENVIRONMENTS, TECHNICAL, environmentLabel } from '../helpers'

const GROUPS = [
  { key: 'staging',   ...ENVIRONMENTS.staging, test: issue => !issue.technical && issue.environment.kind === 'staging' },
  { key: 'preview',   ...ENVIRONMENTS.preview, test: issue => !issue.technical && issue.environment.kind === 'preview' },
  { key: 'none',      ...ENVIRONMENTS.none,    test: issue => !issue.technical && !['preview', 'staging'].includes(issue.environment.kind) },
  { key: 'technical', ...TECHNICAL,            test: issue => issue.technical }
]

const Count = ({ issue }) => {
  const { done, total } = progress(issue.points)
  const validated = isValidated(issue)

  if (issue.technical) return <span className="ml-auto shrink-0 font-semibold text-slate-600">{TECHNICAL.label}</span>
  if (total === 0 && !validated) return <span className="ml-auto shrink-0 font-semibold text-slate-500">Points à venir</span>

  return (
    <span className={clsx('ml-auto inline-flex shrink-0 items-center gap-1 font-semibold tabular-nums', validated ? 'text-emerald-700' : 'text-brand-600')}>
      {validated && <FontAwesomeIcon icon="circle-check" className="text-[11px]" />}
      {total > 0 ? `${done}/${total}` : 'Validée'}
    </span>
  )
}

const RunItem = ({ issue, active, onSelect }) => {

  return (
    <button
      type="button"
      onClick={() => onSelect(issue.number)}
      className={clsx(
        'block w-full rounded-xl border-[1px] px-[16px] py-[12px] text-left font-sans transition focus:outline-none',
        active
          ? 'border-brand-500 bg-brand-50 shadow-[inset_0_0_0_1px_theme(colors.brand.500)]'
          : 'border-slate-200 bg-white hover:border-slate-300'
      )}
    >
      <span className="line-clamp-2 block text-[14px] font-semibold leading-snug text-slate-900">{issue.title}</span>
      <span className="mt-1.5 flex items-center gap-2.5 text-[11.5px] leading-snug text-slate-500">
        <span className="shrink-0 font-semibold tabular-nums text-slate-600">#{issue.number}</span>
        <span className="min-w-0 truncate">{environmentLabel(issue.environment)}</span>
        {issue.csm && (
          <span className="inline-flex min-w-0 items-center gap-1 truncate">
            <FontAwesomeIcon icon="user" className="text-[10px] text-slate-400" />
            {issue.csm}
          </span>
        )}
        {issue.tickets?.length > 0 && (
          <span className="inline-flex shrink-0 items-center gap-1" title={issue.tickets.map(ticket => ticket.account).join(', ')}>
            <FontAwesomeIcon icon="headset" className="text-[10px] text-slate-400" />
            #{issue.tickets[0].id}{issue.tickets.length > 1 && ` +${issue.tickets.length - 1}`}
          </span>
        )}
        <Count issue={issue} />
      </span>
      {!issue.technical && issue.points.length > 0 && <ProgressBar points={issue.points} className="mt-2" />}
    </button>
  )
}

const RunList = ({ issues, selected, onSelect }) => (
  <div className="space-y-4">
    {GROUPS.map(group => {
      const items = issues.filter(group.test)
      if (items.length === 0) return null

      return (
        <section key={group.key}>
          <h2 className="m-0 mb-2 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <FontAwesomeIcon icon={group.icon} className="text-[10px]" />
            {group.group}
            <span className="tabular-nums text-slate-400">{items.length}</span>
          </h2>
          <div className="space-y-2">
            {items.map(issue => (
              <RunItem key={issue.number} issue={issue} active={issue.number === selected} onSelect={onSelect} />
            ))}
          </div>
        </section>
      )
    })}
  </div>
)

export default RunList
